import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * Line icons traced from `Sign Request Detail.dc.html`. The design draws each
 * one as an inline stroked SVG rather than a Material glyph, so the shapes live
 * here instead of pulling near-matches from `@mui/icons-material`.
 *
 * All of them inherit `currentColor`; size comes from the caller's `sx`.
 */

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** Breadcrumb "back to Sign Requests" arrow. */
export const ArrowLeftGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M19 12H5M12 19l-7-7 7-7" />
  </SvgIcon>
);

/** Breadcrumb separator. */
export const ChevronRightGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M9 6l6 6-6 6" />
  </SvgIcon>
);

/** Card collapse chevron. */
export const ChevronDownGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.2} d="M6 9l6 6 6-6" />
  </SvgIcon>
);

/** Document with a folded corner — header tile and document rows. */
export const DocumentGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={1.8}
      d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
    />
    <path {...strokeProps} strokeWidth={1.8} d="M14 3v5h5" />
  </SvgIcon>
);

/** "Requested by" bust. */
export const UserGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
    />
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="7" r="4" />
  </SvgIcon>
);

/** "Sent …" calendar. */
export const CalendarGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="3"
      y="4"
      width="18"
      height="18"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M16 2v4M8 2v4M3 10h18" />
  </SvgIcon>
);

/** "Expires …" clock. */
export const ClockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2} d="M12 7v5l3 3" />
  </SvgIcon>
);

/** "Send Reminder" bell. */
export const BellGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
    />
    <path {...strokeProps} strokeWidth={2} d="M13.7 21a2 2 0 0 1-3.4 0" />
  </SvgIcon>
);

/** Download arrow onto a tray. */
export const DownloadGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M12 3v12" />
    <path {...strokeProps} strokeWidth={2} d="M7 11l5 5 5-5" />
    <path {...strokeProps} strokeWidth={2} d="M5 21h14" />
  </SvgIcon>
);

/** Document "Preview" eye. */
export const EyeGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
    />
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="3" />
  </SvgIcon>
);

/** Vertical three-dot overflow trigger — filled, not stroked. */
export const KebabGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </SvgIcon>
);

/** Completion banner tick. */
export const CheckGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2.4} d="M20 6 9 17l-5-5" />
  </SvgIcon>
);

/** Declined / voided / expired banner mark. */
export const AlertCircleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <circle {...strokeProps} strokeWidth={2.2} cx="12" cy="12" r="9" />
    <path {...strokeProps} strokeWidth={2.2} d="M12 8v5M12 16h.01" />
  </SvgIcon>
);

/** "SIGNS IN ORDER" badge — stacked lines with a down arrow. */
export const SigningOrderGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M3 6h13M3 12h13M3 18h13" />
    <path {...strokeProps} strokeWidth={2} d="M19 4v16M16 17l3 3 3-3" />
  </SvgIcon>
);

/** "APPROVES BEFORE SIGNING STARTS" badge. */
export const CheckCircleGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={2} d="M9 12l2 2 4-4" />
    <circle {...strokeProps} strokeWidth={2} cx="12" cy="12" r="9" />
  </SvgIcon>
);

/** Padlock beside a recipient's security method. */
export const LockGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <rect
      {...strokeProps}
      strokeWidth={2}
      x="4"
      y="11"
      width="16"
      height="10"
      rx="2"
    />
    <path {...strokeProps} strokeWidth={2} d="M8 11V7a4 4 0 0 1 8 0v4" />
  </SvgIcon>
);

/** Speech bubble on the decline-reason note. */
export const MessageGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path
      {...strokeProps}
      strokeWidth={2}
      d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
    />
  </SvgIcon>
);

/** Audit-trail tile — clock with a rewind arrow. */
export const HistoryGlyph = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 24 24" {...props}>
    <path {...strokeProps} strokeWidth={1.9} d="M3 12a9 9 0 1 0 3-6.7" />
    <path {...strokeProps} strokeWidth={1.9} d="M3 4v5h5" />
    <path {...strokeProps} strokeWidth={1.9} d="M12 7v5l3 3" />
  </SvgIcon>
);
